import React, { useState, useEffect } from 'react';

const CommunityFeatures = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const features = [
    {
      id: 0,
      icon: "/images/img_mingcute_ai_line.png",
      title: "Gothram Matching Engine",
      description: "Our database maps every single family lineage of the community to ensure strictly compliant customary matching."
    },
    {
      id: 1,
      icon: "/images/img_mingcute_leo_fill.png",
      title: "Jathagam & Horoscope Matcher",
      description: "Automated 10-Porutham Astro compatibility calculator providing accurate horoscope compatibility instantly."
    },
    {
      id: 2,
      icon: "/images/img_mdi_verified_user_gray_900.png",
      title: "Verified Family Network",
      description: "Multi-factor authentication with ID checks, OTP verification, and family reference checks."
    }
  ];

  useEffect(() => {
    if (isHovered) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % features.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isHovered, features.length]);

  return (
    <section className="relative w-full bg-cover bg-center py-8 sm:py-10 md:py-12 lg:py-16">
      <div className="w-full max-w-[1360px] mx-auto px-4 sm:px-6">
        <div className="backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 lg:p-14">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">

            <div className="w-full lg:w-1/2 relative flex flex-col items-center justify-center py-4 sm:py-6">
              {/* Background Blob Shape */}
              <div className="absolute inset-0 flex justify-center items-center pointer-events-none -z-10">
                <img
                  src="/images/img_engineered_for_blob.png"
                  alt="Background Shape"
                  className="w-[90%] max-w-[500px] h-auto object-contain opacity-60"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {/* Dual Image Container */}
              <div className="relative w-full max-w-[460px] flex gap-3 sm:gap-4 mb-3 z-10">
                {/* Left Card 1: App Preview */}
                <div className="w-1/2 bg-white/40 backdrop-blur-md border-2 border-white rounded-2xl sm:rounded-3xl shadow-lg overflow-hidden h-[180px] xs:h-[210px] sm:h-[270px] flex items-center justify-center transition-all duration-500">
                  <img
                    src="/images/img_smartphone_disp.png"
                    alt="App Preview"
                    className="w-full h-full object-cover rounded-xl sm:rounded-2xl"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/img_profile_img.png';
                    }}
                  />
                </div>

                {/* Right Card 2: Transparent Couple */}
                <div className="w-1/2 relative h-[180px] xs:h-[210px] sm:h-[270px] flex flex-col transition-all duration-500">
                  <img
                    src="/images/abt_1.webp"
                    alt="Couple"
                    loading="lazy"
                    className="absolute -top-4 sm:-top-10 left-1/2 transform -translate-x-1/2 w-[110%] sm:w-[115%] max-w-none h-auto object-contain pointer-events-none z-10"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/images/img_couple_smiling.webp';
                    }}
                  />
                </div>
              </div>

              {/* Bottom Overlapping CTA Card */}
              <div className="relative w-full max-w-[440px] -mt-6 sm:-mt-12 z-20">
                <div className="relative overflow-hidden mt-3 sm:mt-5 rounded-2xl sm:rounded-3xl p-4 sm:p-7 text-white shadow-2xl border-2 border-white/40 bg-gradient-to-r from-[#8A216B] via-[#F03F16] to-[#F03F16]">
                  <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_15%_100%,rgba(255,255,255,0.92)_0%,rgba(255,255,255,0.72)_30%,rgba(255,255,255,0.30)_55%,transparent_78%)]" />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/60 via-white/15 to-transparent" />

                  <div className="relative z-10">
                    <h3 className="text-lg sm:text-2xl font-bold mb-1.5 sm:mb-2 tracking-tight text-white">
                      Ready To Find Your Match?
                    </h3>

                    <p className="text-xs sm:text-sm text-white/90 mb-3 sm:mb-5 leading-relaxed">
                      Discover verified profiles, trusted matchmaking, and meaningful connections in one secure platform.
                    </p>

                    <div className="flex justify-end">
                      <button className="px-4 sm:px-6 py-1.5 sm:py-2.5 bg-gradient-to-r from-[#8a216b] to-[#f03f16] border border-white/50 rounded-full text-white font-semibold text-xs sm:text-sm flex items-center gap-1.5 shadow-lg transition-all cursor-pointer">
                        Discover Your Match
                        <span className="text-xs sm:text-sm font-bold">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Content & Animated Features */}
            <div
              className="w-full lg:w-1/2"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <p className="text-xs sm:text-base font-semibold uppercase tracking-wide bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent mb-2 sm:mb-4">
                Engineered For
              </p>
              <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-[48px] font-semibold text-text-primary capitalize mb-3 sm:mb-6 leading-tight">
                Anuppa Gounder Community
              </h2>
              <p className="text-xs sm:text-lg text-text-primary/60 mb-6 sm:mb-12 leading-relaxed">
                Tamil Nadu&apos;s trusted matrimonial platform for the Anuppa Gounder community, connecting verified brides and grooms through tradition, trust, and smart matchmaking.
              </p>

              {/* Features List with Color Animation & Progress Line */}
              <div className="space-y-4 sm:space-y-6">
                {features.map((feature, idx) => {
                  const isActive = activeIndex === idx;
                  return (
                    <div
                      key={feature.id}
                      onClick={() => setActiveIndex(idx)}
                      className="group flex items-stretch gap-3.5 sm:gap-6 cursor-pointer py-1.5"
                    >
                      {/* Animated Side Progress Line */}
                      <div className="relative w-1 sm:w-[5px] rounded-full self-stretch bg-gray-200 overflow-hidden flex-shrink-0">
                        <div
                          className={`absolute top-0 left-0 w-full bg-gradient-to-b from-[#8a216b] to-[#f03f16] rounded-full transition-all duration-700 ease-in-out ${isActive ? 'h-full opacity-100' : 'h-0 opacity-0'
                            }`}
                        />
                      </div>

                      <div className="flex-1 flex items-start gap-3 sm:gap-5 py-1">
                        {/* Icon with animated color transition */}
                        <div className="flex-shrink-0 mt-0.5">
                          <div
                            className={`w-7 sm:w-9 md:w-10 h-7 sm:h-9 md:h-10 transition-all duration-500 ${isActive
                              ? 'bg-gradient-to-r from-[#8a216b] to-[#f03f16] scale-110'
                              : 'bg-gray-800 opacity-60'
                              }`}
                            style={{
                              WebkitMaskImage: `url(${feature.icon})`,
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center',
                              maskImage: `url(${feature.icon})`,
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center'
                            }}
                          />
                        </div>

                        <div className="flex-1">
                          {/* Title with Gradient Text Animation */}
                          <h3
                            className={`text-base sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2 transition-all duration-500 ${isActive
                              ? 'bg-gradient-to-r from-[#8a216b] to-[#f03f16] bg-clip-text text-transparent'
                              : 'text-text-primary group-hover:text-gray-800'
                              }`}
                          >
                            {feature.title}
                          </h3>

                          {/* Description */}
                          <p
                            className={`text-xs sm:text-base leading-relaxed transition-all duration-500 ${isActive ? 'text-text-primary/80 font-medium' : 'text-text-primary/50'
                              }`}
                          >
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityFeatures;